import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Filter, Search, X } from "lucide-react";

// Filtro colonna riutilizzabile: popover + checkbox (multi-selezione OR) oppure ricerca testo / soglia numerica.
export default function ColumnFilter({
  type = "select", // "select" | "text" | "number"
  label,
  options = [],
  value,
  onChange,
  testid,
  align = "start",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const isSelect = type === "select";
  const active = isSelect ? (value?.length || 0) > 0 : value !== "" && value != null;
  const count = isSelect ? value?.length || 0 : 0;

  const filteredOptions = useMemo(() => {
    if (!isSelect) return [];
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, search, isSelect]);

  const toggle = (v) => {
    const set = new Set(value || []);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange([...set]);
  };
  const clear = () => onChange(isSelect ? [] : "");

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className={`inline-flex items-center gap-1 rounded p-1 transition-colors ${active ? "text-blue-700" : "text-slate-400 hover:text-slate-600"}`}
          aria-label={`Filtra ${label}`}
          title={`Filtra ${label}`}
        >
          <Filter className="h-3.5 w-3.5" fill={active ? "currentColor" : "none"} />
          {active && count > 0 && (
            <span className="text-[10px] font-semibold leading-none bg-blue-100 text-blue-800 rounded-full px-1 py-0.5 tabular-nums">{count}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-0" data-testid={`${testid}-content`}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
          {active && (
            <button data-testid={`${testid}-clear`} onClick={clear} className="text-xs text-slate-400 hover:text-rose-600 inline-flex items-center gap-1">
              <X className="h-3 w-3" /> Azzera
            </button>
          )}
        </div>

        {isSelect ? (
          <div className="p-2">
            {options.length > 6 && (
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…" className="h-8 pl-7 text-xs" data-testid={`${testid}-search`} />
              </div>
            )}
            <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-slate-100">
              <button data-testid={`${testid}-select-all`} onClick={() => onChange(options.map((o) => o.value))} className="text-[11px] font-medium text-blue-700 hover:underline">Seleziona tutto</button>
              <button data-testid={`${testid}-deselect-all`} onClick={() => onChange([])} className="text-[11px] text-slate-400 hover:underline">Deseleziona</button>
            </div>
            <div className="max-h-56 overflow-auto space-y-0.5">
              {filteredOptions.length === 0 && <p className="text-xs text-slate-400 px-1 py-2">Nessun valore</p>}
              {filteredOptions.map((o) => (
                <label key={o.value} data-testid={`${testid}-option-${o.value}`} className="flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={(value || []).includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                  <span className="text-xs text-slate-700 truncate">{o.label}</span>
                </label>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-3">
            <Input
              type={type === "number" ? "number" : "text"}
              value={value ?? ""}
              autoFocus
              onChange={(e) => onChange(e.target.value)}
              placeholder={type === "number" ? "≥ soglia" : "Cerca…"}
              className="h-8 text-xs"
              data-testid={`${testid}-input`}
            />
            {type === "number" && <p className="text-[11px] text-slate-400 mt-1.5">Mostra le righe con valore ≥ alla soglia.</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
